import React from 'react'
import { PermissionGroupIcon, PersonAvatar, Pill } from '@sketch/components'

import {
  useGetShareMembershipsQuery,
  WorkspaceMinimalFragment,
  ProjectFragment,
  StorageItemFragment,
} from '@sketch/gql-types'

import {
  Title,
  Row,
  List,
  Name,
  Permission,
  PlaceholderAvatar,
  PlaceholderWorkspaceAvatar,
  ErrorText,
  MoreMembers,
  StyledProjectRow,
  StyledWorkspaceRow,
  Section,
} from './StorageItemAccessList.styles'

import { getPermissionsLabel } from './permissions'

type Project = Pick<
  ProjectFragment,
  | 'identifier'
  | 'name'
  | 'workspaceAccessLevel'
  | 'projectType'
  | 'topLevelProject'
>

interface StorageItemAccessListProps {
  item: Pick<StorageItemFragment, '__typename' | 'identifier'>
  destination: {
    project: Project | null
    workspace: WorkspaceMinimalFragment
  }
  currentLocation: {
    project: Project | null
    workspace: WorkspaceMinimalFragment
  }
  hideSharePermissions?: boolean
}

const PERSONAL_MIN_MEMBERSHIPS = 1
const MEMBERSHIPS_PAGE_SIZE = 20
const MEMBERSHIPS_LIMIT = 50

const Wrapper: React.FC = ({ children }) => (
  <Section aria-label="access-list">
    <Title>These people will have access:</Title>
    <List>{children}</List>
  </Section>
)

const PersonPlaceholder = () => (
  <Row>
    <PlaceholderAvatar size="24px" flavour="color" name="" />
    <span className="sr-only">placeholder item</span>
  </Row>
)

const createPersonPlaceholder = (size: number) =>
  new Array(size)
    .fill(null)
    .map((_, index) => <PersonPlaceholder key={index} />)

interface SharePermissionsListProps {
  shareIdentifier: string
}
function SharePermissionsList(props: SharePermissionsListProps) {
  const { shareIdentifier } = props
  const { error, data, loading } = useGetShareMembershipsQuery({
    // it is possible that share memberships has changed from the last visit (even if we made the change ourselves)
    // therefore we want always to fetch the latest data even if we have it in the cache.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      shareIdentifier,
      limit: MEMBERSHIPS_LIMIT,
    },
  })

  const share = data?.share
  const memberships = share?.members?.entries || []
  const membershipCount = share?.members?.meta.totalCount

  if (loading && memberships.length === 0) {
    return (
      <>
        <Row>
          <PlaceholderWorkspaceAvatar size="24px" workspaceName="  " />
        </Row>
        {createPersonPlaceholder(membershipCount || PERSONAL_MIN_MEMBERSHIPS)}
      </>
    )
  }

  if (error || !share || !share.members) {
    return <ErrorText>There was a problem loading the access list</ErrorText>
  }

  return (
    <>
      {memberships.map(member => {
        if (member?.__typename === 'PermissionGroupRelationShare') {
          const { identifier, permissionGroup, accessLevel } = member

          return (
            <Row key={identifier}>
              <PermissionGroupIcon name={permissionGroup.name} size="24px" />

              <Name>{permissionGroup.name}</Name>
              <Permission>{getPermissionsLabel(accessLevel)}</Permission>
            </Row>
          )
        }

        const { identifier, workspaceMembership, privateAccessLevel, email } =
          member

        const { user } = workspaceMembership || {}
        const name = user?.name || email || ''
        const role = workspaceMembership?.role

        return (
          <Row key={identifier}>
            {user?.avatar?.small ? (
              <PersonAvatar
                size="24px"
                flavour="image"
                src={user.avatar.small}
                name={name}
              />
            ) : (
              <PersonAvatar
                size="24px"
                flavour="color"
                name={name}
                color="default"
              />
            )}
            <Name>
              {name} {role === 'GUEST' && <Pill variant="guest">Guest</Pill>}
            </Name>
            <Permission>{getPermissionsLabel(privateAccessLevel)}</Permission>
          </Row>
        )
      })}

      {share.members.meta.totalCount > MEMBERSHIPS_LIMIT && (
        <MoreMembers>{`${
          share.members.meta.totalCount - MEMBERSHIPS_LIMIT
        } More`}</MoreMembers>
      )}

      {loading &&
        createPersonPlaceholder(
          Math.min(
            MEMBERSHIPS_PAGE_SIZE,
            share.members.meta.totalCount - share.members.entries.length
          )
        )}
    </>
  )
}

export function StorageItemAccessList({
  item,
  destination,
  hideSharePermissions = true,
}: Pick<
  StorageItemAccessListProps,
  'item' | 'destination' | 'hideSharePermissions'
>) {
  return (
    <Wrapper>
      {destination?.project?.projectType === 'PERSONAL_DRAFTS' ||
      destination?.project?.workspaceAccessLevel === 'NONE' ? (
        <StyledProjectRow
          topLevelProject={
            destination?.project.topLevelProject || destination?.project
          }
          workspace={destination.workspace}
          actions={<Permission>Default</Permission>}
        />
      ) : (
        <StyledWorkspaceRow
          showAllMembers
          workspace={destination.workspace}
          actions={<Permission>Default</Permission>}
        />
      )}
      {!hideSharePermissions && item.__typename === 'Share' && (
        <SharePermissionsList shareIdentifier={item.identifier} />
      )}
    </Wrapper>
  )
}
